import { SubjectIllustration, Table, TableColumns } from '@bp/ui-components';
import { FragmentType, graphql } from 'client/gql';
import { useTranslation } from 'react-i18next';

const AbsenceEvaluationMemberSummaryTableFragment = graphql(`
  fragment AbsenceEvaluationMemberSummaryTableFields on Query {
    classes(where: $classesWhere) {
      __typename
      uuid
      membersConnection {
        __typename
        edges {
          __typename
          node {
            __typename
            uuid
            selectName
          }
        }
      }
    }
  }
`);

type AbsenceEvaluationMemberSummaryTableProps = {
  query: FragmentType<typeof AbsenceEvaluationMemberSummaryTableFragment> | undefined;
};

type AbsenceEvaluationMemberSummaryTableType = {
  uuid: string;
  name: string;
  hoursSum: number;
  fullyPresent: number;
  percentage: number;
  partialPresent: number;
  fullyAbsent: number;
  hoursExcused: number;
};

export const AbsenceEvaluationMemberSummaryTable = ({ query }: AbsenceEvaluationMemberSummaryTableProps) => {
  const { t } = useTranslation();

  const columns: TableColumns<AbsenceEvaluationMemberSummaryTableType>[] = [
    {
      id: 'name',
      accessorKey: 'name',
      canExpand: true,
      header: t('common.designation'),
    },
    {
      id: 'hoursSum',
      accessorKey: 'hoursSum',
      header: t('absences.evaluation.hoursSum'),
      type: 'number',
    },
    {
      id: 'fullyPresent',
      accessorKey: 'fullyPresent',
      header: t('absences.evaluation.fullyPresent'),
      type: 'number',
    },
    {
      id: 'percent',
      accessorKey: 'percent',
      header: t('common.percentage'),
      accessorFn: ({ percentage }) => `${percentage}%`,
      size: 80,
    },
    {
      id: 'partialPresent',
      accessorKey: 'partialPresent',
      header: t('absences.evaluation.partialPresent'),
      type: 'number',
    },
    {
      id: 'fullyAbsent',
      accessorKey: 'fullyAbsent',
      header: t('absences.evaluation.fullyAbsent'),
      type: 'number',
    },
    {
      id: 'hoursExcused',
      accessorKey: 'hoursExcused',
      header: t('absences.evaluation.hoursExcused'),
      type: 'number',
    },
  ];

  const data: AbsenceEvaluationMemberSummaryTableType[] = [].map(() => ({
    uuid: '',
    name: '',
    hoursSum: 0,
    fullyPresent: 0,
    percentage: 0,
    partialPresent: 0,
    fullyAbsent: 0,
    hoursExcused: 0,
  }));

  return (
    <Table<AbsenceEvaluationMemberSummaryTableType>
      data={data}
      columns={columns}
      emptyStateSettings={{ icon: <SubjectIllustration />, title: t('subjects.noSubjects'), subtitle: '' }}
      customPadding='var(--spacing-6)'
    />
  );
};
